// Reusable input validation utilities for contact forms

/**
 * Handle keydown to prevent invalid characters from being typed
 * @param {Event} e - The keydown event
 * @param {string} fieldName - The name of the field (name, phone, etc.)
 */
export const handleInputKeyDown = (e, fieldName) => {
  const { key } = e;
  
  // Allow special keys (backspace, delete, arrow keys, etc.)
  if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape'].includes(key)) {
    return;
  }
  
  // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
  if (e.ctrlKey || e.metaKey) {
    return;
  }
  
  if (fieldName === 'name') {
    // Allow letters and spaces for full names
    if (!/^[a-zA-Z\s]$/.test(key)) {
      e.preventDefault();
    }
  } else if (fieldName === 'phone') {
    // Only allow digits
    if (!/^[\d]$/.test(key)) {
      e.preventDefault();
    }
  }
};

/**
 * Handle input change with basic processing (no real-time validation)
 * @param {Event} e - The change event
 * @param {Object} formData - Current form data
 * @param {Function} setFormData - Function to update form data
 * @param {Function} setFormErrors - Function to update form errors
 * @returns {Object} - Updated form data
 */
export const handleInputChange = (e, formData, setFormData, setFormErrors) => {
  const { name, value } = e.target;
  let updatedValue = value;
  
  // Basic processing based on field type
  if (name === 'phone') {
    // Limit to 10 digits maximum
    if (value.length > 10) {
      updatedValue = value.slice(0, 10);
    }
  }
  
  // Update form data
  const newFormData = {
    ...formData,
    [name]: updatedValue
  };
  setFormData(newFormData);
  
  // Clear errors based on field type
  setFormErrors(prev => {
    const newErrors = { ...prev };
    
    // Clear error for the current field
    newErrors[name] = '';
    
    // Special handling for email/phone - if either has content, clear both "required" errors
    if (name === 'email' || name === 'phone') {
      const emailValue = name === 'email' ? updatedValue : newFormData.email;
      const phoneValue = name === 'phone' ? updatedValue : newFormData.phone;
      
      // If either email or phone has content, clear both "required" errors
      if (emailValue.trim() || phoneValue.trim()) {
        if (newErrors.email === 'Either email or phone number is required') {
          newErrors.email = '';
        }
        if (newErrors.phone === 'Either email or phone number is required') {
          newErrors.phone = '';
        }
      }
    }
    
    return newErrors;
  });
  
  return { updatedValue };
};


/**
 * Get input field props for consistent validation
 * @param {string} fieldName - The name of the field
 * @param {Object} formData - Current form data
 * @param {Object} formErrors - Current form errors
 * @param {Function} handleChange - Change handler function
 * @returns {Object} - Input field props
 */
export const getInputProps = (fieldName, formData, formErrors, handleChange) => {
  const baseProps = {
    value: formData[fieldName] || '',
    onChange: handleChange,
    onKeyDown: (e) => handleInputKeyDown(e, fieldName),
  };

  if (fieldName === 'name') {
    return {
      ...baseProps,
      type: 'text',
      placeholder: 'Full Name',
      title: 'Please enter only letters',
    };
  } else if (fieldName === 'phone') {
    return {
      ...baseProps,
      type: 'tel',
      placeholder: 'Phone Number',
      title: 'Please enter only numbers',
    };
  }

  return baseProps;
};

/**
 * Get error class for input fields
 * @param {string} fieldName - The name of the field
 * @param {Object} formErrors - Current form errors
 * @param {string} baseClass - Base CSS class name
 * @returns {string} - CSS class with error state
 */
export const getInputErrorClass = (fieldName, formErrors, baseClass = '') => {
  return `${baseClass} ${formErrors[fieldName] ? 'error' : ''}`.trim();
};
